/**
 * The gamepads of the page: one reader for the whole app (padReader.ts), and who gets what
 * the pads do.
 *
 * - Consumers (listen) get every edge, the one added last first, until one takes it: the
 *   shell's navigator (app/padNavigation.ts) sits at the bottom, a game screen
 *   (GameInput) on top while it is mounted — it lets the edges through while a dialog of the
 *   shell covers it.
 * - Who reads: a game screen reads the pads itself in its frame loop (drive), right before
 *   the simulation steps, so a press lands on its tick. Otherwise the hub's own animation
 *   frame loop does, and only while a pad is connected and the page is visible: a phone
 *   without a pad never polls. (Without install() — the engine harness, tests — a driver reads
 *   on every call.)
 * - The first press of a page load announces the pad (onFirstUse: the shell's toast), and a
 *   press puts the page into pad mode: PAD_MODE_ATTR on <html> rings the focused control, which
 *   browsers do not always do for focus moved by script. A mouse click or a touch ends it.
 */
import { type ReadonlySignal, signal } from '@preact/signals';
import { type GamepadList, type PadEdge, PadReader } from './padReader';
import type { PadStyle } from './pads';

/** On <html> while a gamepad is in use: the focused control shows a ring (base.css). */
export const PAD_MODE_ATTR = 'data-gamepad';

export interface PadConsumer {
  /** Takes an edge (true) or lets the consumers below have it (false). */
  onPad(edge: PadEdge): boolean;
}

/** A pad's rumble (GamepadHapticActuator.playEffect, "dual-rumble"). */
export interface PadRumble {
  durationMs: number;
  /** 0..1, the low-frequency motor. */
  strong: number;
  /** 0..1, the high-frequency motor. */
  weak: number;
}

/** What the hub needs from the browser (a fake in tests). */
export interface PadWindow {
  readonly document: Pick<
    Document,
    'visibilityState' | 'documentElement' | 'addEventListener' | 'removeEventListener'
  >;
  readonly performance: { now(): number };
  requestAnimationFrame(callback: () => void): number;
  cancelAnimationFrame(handle: number): void;
  addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void;
  removeEventListener(type: string, listener: () => void, options?: EventListenerOptions): void;
}

const NO_PADS: GamepadList = [];
/**
 * After `gamepadconnected` the hub reads for at least this long, even while the browser does
 * not list the pad yet: one that lists it a frame late is never missed.
 */
const CONNECT_GRACE_MS = 1000;

/** navigator.getGamepads(), or nothing where there is no Gamepad API or it is not allowed. */
export function browserGamepads(): GamepadList {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
      return NO_PADS;
    }
    return navigator.getGamepads() ?? NO_PADS;
  } catch {
    // Blocked by a permissions policy (an embedding page).
    return NO_PADS;
  }
}

export class GamepadHub {
  private readonly reader = new PadReader();
  private readonly edges: PadEdge[] = [];
  private readonly consumers: PadConsumer[] = [];
  private readonly styleSignal = signal<PadStyle | null>(null);
  private drivers = 0;
  private win: PadWindow | null = null;
  /** Handle of the hub's own loop's next frame (0: not running). */
  private frame = 0;
  /** A pad may be connected (an installed hub reads only then). */
  private live = false;
  /** When `gamepadconnected` came last (see CONNECT_GRACE_MS). */
  private connectedAt = Number.NEGATIVE_INFINITY;
  private used = false;
  private padMode = false;
  private lastPad: number | null = null;
  private firstUse: ((style: PadStyle) => void) | null = null;

  /** How the pad used last names its buttons; null until a pad is used. */
  readonly style: ReadonlySignal<PadStyle | null> = this.styleSignal;

  constructor(private readonly list: () => GamepadList = browserGamepads) {}

  /** Wires the browser (pads coming, the page hiding, pad mode); returns the uninstall. */
  install(win: PadWindow): () => void {
    this.win = win;
    const connected = () => {
      this.live = true;
      this.connectedAt = win.performance.now();
      this.schedule();
    };
    const visibility = () => this.schedule();
    const pointer = () => this.setPadMode(false);
    const capture: AddEventListenerOptions = { capture: true, passive: true };
    win.addEventListener('gamepadconnected', connected);
    win.addEventListener('pointerdown', pointer, capture);
    win.document.addEventListener('visibilitychange', visibility);
    // Some browsers list the pads that were connected before the page loaded.
    if (hasPad(this.list())) connected();
    return () => {
      win.removeEventListener('gamepadconnected', connected);
      win.removeEventListener('pointerdown', pointer, capture);
      win.document.removeEventListener('visibilitychange', visibility);
      if (this.frame !== 0) win.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.setPadMode(false);
      this.win = null;
    };
  }

  /** Adds a consumer on top; returns the function that removes it. */
  listen(consumer: PadConsumer): () => void {
    this.consumers.push(consumer);
    return () => {
      const index = this.consumers.lastIndexOf(consumer);
      if (index >= 0) this.consumers.splice(index, 1);
    };
  }

  /** The caller reads the pads itself (poll, every frame) until the returned function runs. */
  drive(): () => void {
    this.drivers++;
    this.schedule();
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.drivers--;
      this.schedule();
    };
  }

  /** The first press of a page load, with the pad's style (once). */
  onFirstUse(callback: (style: PadStyle) => void): () => void {
    this.firstUse = callback;
    return () => {
      if (this.firstUse === callback) this.firstUse = null;
    };
  }

  /** Reads the pads at `now` (performance.now() timebase) and hands every change on. */
  poll(now: number): void {
    // An installed hub hears pads come; until then there is nothing to read.
    if (this.win && !this.live) return;
    const edges = this.edges;
    edges.length = 0;
    this.reader.read(this.list(), now, edges);
    const win = this.win;
    if (win && this.reader.connected === 0) {
      this.live = win.performance.now() - this.connectedAt < CONNECT_GRACE_MS;
    }
    if (edges.length === 0) return;
    // Consumers may come and go while edges are handed on (a press that leaves a screen).
    const consumers = this.consumers.slice();
    for (const edge of edges) {
      if (edge.down && !edge.repeat) this.noteUse(edge);
      for (let i = consumers.length - 1; i >= 0; i--) {
        if (consumers[i]!.onPad(edge)) break;
      }
    }
  }

  /** A short rumble of the pad used last, where the browser can (a crash). */
  rumble(effect: PadRumble): void {
    const index = this.lastPad;
    if (index === null) return;
    try {
      const pads = this.list();
      let actuator: GamepadHapticActuator | null | undefined;
      for (let i = 0; i < pads.length; i++) {
        const pad = pads[i];
        if (pad?.index === index) actuator = pad.vibrationActuator;
      }
      if (!actuator || typeof actuator.playEffect !== 'function') return;
      const done = actuator.playEffect('dual-rumble', {
        duration: effect.durationMs,
        strongMagnitude: effect.strong,
        weakMagnitude: effect.weak,
      });
      // Not every pad can (NotSupportedError); nothing to tell anybody.
      void Promise.resolve(done).catch(() => {});
    } catch {
      // A rumble is never worth an error.
    }
  }

  private noteUse(edge: PadEdge): void {
    this.lastPad = edge.pad;
    if (this.styleSignal.peek() !== edge.style) this.styleSignal.value = edge.style;
    this.setPadMode(true);
    if (this.used) return;
    this.used = true;
    this.firstUse?.(edge.style);
  }

  private setPadMode(on: boolean): void {
    const root = this.win?.document.documentElement;
    if (!root || this.padMode === on) return;
    this.padMode = on;
    if (on) root.setAttribute(PAD_MODE_ATTR, '');
    else root.removeAttribute(PAD_MODE_ATTR);
  }

  /**
   * Runs the hub's own loop while it is wanted: installed, a pad connected, the page visible
   * and no game screen reading the pads itself.
   */
  private schedule(): void {
    const win = this.win;
    const wanted =
      win !== null && this.live && this.drivers === 0 && win.document.visibilityState !== 'hidden';
    if (wanted && this.frame === 0) this.frame = win.requestAnimationFrame(this.tick);
    else if (!wanted && this.frame !== 0) {
      win?.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  private readonly tick = (): void => {
    this.frame = 0;
    const win = this.win;
    if (!win) return;
    // The next frame first: a press whose handler throws must not stop the pads (the read
    // that finds no pad any more lets the loop end a frame later).
    this.schedule();
    this.poll(win.performance.now());
  };
}

function hasPad(list: GamepadList): boolean {
  for (let i = 0; i < list.length; i++) if (list[i]?.connected) return true;
  return false;
}

/** The app's gamepads (installed by createApp; the engine reads them on its own without it). */
export const gamepads = new GamepadHub();
